import { Link } from "react-router-dom";
import { ArrowRight, Search } from "lucide-react";
import { useState, useMemo } from "react";
import SectionHeading from "@/components/SectionHeading";
import Reveal from "@/components/Reveal";
import Seo from "@/components/Seo";
import { useContent } from "@/lib/content-context";

const HERO = "https://images.pexels.com/photos/27878405/pexels-photo-27878405.jpeg?auto=compress&cs=tinysrgb&w=1800&q=80";

export default function DestinationsPage() {
  const { destinations } = useContent();
  const [q, setQ] = useState("");
  const [country, setCountry] = useState("All");
  const countries = useMemo(() => ["All", ...Array.from(new Set(destinations.map((d) => d.country)))], [destinations]);

  const filtered = useMemo(() => {
    return destinations.filter((d) => {
      const matchQ = q ? d.name.toLowerCase().includes(q.toLowerCase()) || d.country.toLowerCase().includes(q.toLowerCase()) : true;
      const matchC = country === "All" ? true : d.country === country;
      return matchQ && matchC;
    });
  }, [q, country, destinations]);

  return (
    <div data-testid="destinations-page">
      <Seo
        title="Air Zimbabwe Destinations from the UK | Simba Travel"
        description="Explore the six Air Zimbabwe destinations served from the United Kingdom — London, Harare, Bulawayo, Victoria Falls, Mutare and Dar es Salaam. Fares, guides and enquiries via Simba Travel."
      />
      <section className="relative h-[60vh] min-h-[420px] -mt-24 flex items-end overflow-hidden">
        <img src={HERO} alt="Victoria Falls" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-midnight to-midnight/30" />
        <div className="relative container-edge pb-14 text-linen">
          <div className="overline text-ochre mb-4">Destinations</div>
          <h1 className="editorial-h1 max-w-3xl">Six Air Zimbabwe destinations.</h1>
          <p className="mt-4 text-linen/80 max-w-xl">From Cape Town to Cairo's doorstep — supported by Air Zimbabwe and our partner-carrier network.</p>
        </div>
      </section>

      {/* Intro — long-form for SEO & context */}
      <section className="bg-linen border-b border-ink/10 pt-14 md:pt-20 pb-10" data-testid="destinations-intro">
        <div className="container-edge grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="overline text-terracotta mb-4">The network</div>
            <h2 className="editorial-h2">Fly from London to the heart of Southern Africa.</h2>
          </div>
          <div className="md:col-span-7 md:pt-2 space-y-6">
            <p className="text-base leading-relaxed text-ink/80 font-light">
              Simba Travel is the appointed United Kingdom General Sales Agent for Air Zimbabwe — the flag carrier of Zimbabwe. From London Gatwick, Air Zimbabwe operates a scheduled nonstop service to Harare (HRE), the airline's primary hub in the heart of Southern Africa. From Harare, the airline's onward regional network puts Victoria Falls, Bulawayo, Mutare and Dar es Salaam within a single-ticket connection, making this one of the most convenient ways to reach Zimbabwe and eastern Tanzania from Britain.
            </p>
            <p className="text-base leading-relaxed text-ink/80 font-light">
              Below is the full network we service from the United Kingdom. Each destination page includes an overview, key attractions, best time to visit, flight information and a direct enquiry form so that our London-based sales desk can build combined itineraries — for example, a leisure traveller looking at Victoria Falls with a two-night Zanzibar extension via Dar es Salaam, or a diaspora passenger flying home to Bulawayo for a family event.
            </p>
            <p className="text-base leading-relaxed text-ink/80 font-light">
              For groups of ten or more, corporate clients, mining, energy, NGO or government travel — our dedicated desks handle fare quotes, group blocks, special service requests and manual PNR builds. All bookings are protected under the Air Zimbabwe conditions of carriage and ticketed via BSP-UK from our London office. Whichever destination you're planning, our contact centre is open 09:00 – 23:00 UK time, seven days a week.
            </p>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="bg-linen border-b border-ink/10 sticky top-[68px] z-30">
        <div className="container-edge py-5 flex flex-col md:flex-row gap-4 md:items-center md:justify-between">
          <div className="flex items-center gap-3 border-b border-ink/20 flex-1 max-w-md">
            <Search className="w-4 h-4 stroke-1 text-muted2" />
            <input
              data-testid="destination-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search destinations or countries…"
              className="bg-transparent w-full py-2 text-sm outline-none placeholder:text-ink/30"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {countries.map((c) => (
              <button
                key={c}
                data-testid={`filter-${c}`}
                onClick={() => setCountry(c)}
                className={`text-[11px] tracking-widest uppercase px-3 py-1.5 border transition-colors ${
                  country === c ? "bg-midnight text-linen border-midnight" : "border-ink/20 hover:border-midnight"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="section-pad bg-linen">
        <div className="container-edge">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filtered.map((d, i) => (
              <Reveal
                key={d.slug}
                delay={(i % 6) * 0.05}
                className="group"
                data-testid={`dest-grid-${d.slug}`}
              >
                <Link to={`/destinations/${d.slug}`} className="block">
                  <div className="relative overflow-hidden bg-midnight aspect-[4/5]">
                    <img
                      src={d.thumb}
                      alt={d.name}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-midnight via-midnight/20 to-transparent" />
                    <div className="absolute top-6 left-6 right-6 flex items-start justify-between text-linen">
                      <div className="text-[10px] tracking-[0.3em] uppercase text-ochre">{d.iata}</div>
                      <ArrowRight className="w-4 h-4 stroke-1 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                    <div className="absolute bottom-6 left-6 right-6 text-linen">
                      <div className="text-xs tracking-widest uppercase text-linen/70">{d.country}</div>
                      <div className="font-serif text-3xl mt-1">{d.name}</div>
                    </div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
          {filtered.length === 0 && (
            <div className="text-center text-muted2 py-20" data-testid="no-results">
              No destinations match your search.
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
